/**
 * IntakeNotesBlock — strukturierter Intake-Notizen-Block (Issue #14, WP-4).
 *
 * 2-Spalten-Layout im Übergabe-Bereich (kind='intake'):
 *   LINKS  optional zuerst der Slot `verantwortlich` (Picker PL/Manager/MTB aus der Übergabe,
 *          #83 — speichern sofort, nicht über den Speichern-Knopf), danach die 5 Textinputs in
 *          Tab-Reihenfolge Vertrieb → fachl.AP → Sparte → QM → Mission Briefing.
 *   RECHTS sonstiges-Textarea, im DOM nach der linken Spalte — kein `tabIndex` gesetzt, die
 *          Tab-Reihenfolge ergibt sich allein aus der Quellreihenfolge (Picker-Slot → 5
 *          Textinputs → Sonstiges).
 *
 * Werte kommen vorbelegt aus `project.intake_handover_fields.uebergabe_intake`
 * (Backend reichert leere Felder mit SF-Defaults an — UI belegt nichts selbst vor).
 *
 * Speichern: Der Backend-PATCH ersetzt `intake_handover_fields` komplett (Drizzle
 * `.set()` auf der jsonb-Spalte, kein Deep-Merge). Daher senden wir das volle
 * gemergte Objekt (`...intakeFields`), damit andere Keys (presales_notes etc.)
 * erhalten bleiben, und setzen `uebergabe_intake.updated_at/_by`.
 */
import type { IntakeHandoverFields } from '@projektverwaltung/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import { useToast } from '../toast.js';
import { AssignableRow } from './assignable-row.js';
import { Card } from './card.js';

function BlockShell({ embedded, children }: { embedded: boolean; children: ReactNode }) {
  return embedded ? <>{children}</> : <Card>{children}</Card>;
}

// Die linken Textfelder in Tab-Reihenfolge (DOM-Reihenfolge = Tab-Reihenfolge). Die QM-Auswahl
// steht im DOM zwischen `sparte` und `mb_ansprechpartner` (QM_NACH).
const TEXT_FIELDS = ['vertrieb', 'fachl_ap', 'sparte', 'mb_ansprechpartner'] as const;
type TextFieldKey = (typeof TEXT_FIELDS)[number];
const QM_NACH: TextFieldKey = 'sparte';

type DraftKey = TextFieldKey | 'sonstiges';
type Draft = Record<DraftKey, string> & { qm_user_id: string | null };

function buildDraft(fields: IntakeHandoverFields): Draft {
  const ui = fields.uebergabe_intake ?? {};
  return {
    vertrieb: ui.vertrieb ?? '',
    fachl_ap: ui.fachl_ap ?? '',
    sparte: ui.sparte ?? '',
    qm_user_id: ui.qm_user_id ?? null,
    mb_ansprechpartner: ui.mb_ansprechpartner ?? '',
    sonstiges: ui.sonstiges ?? '',
  };
}

export function IntakeNotesBlock({
  projectId,
  intakeFields,
  canEdit,
  isReader,
  onSaved,
  embedded = false,
  verantwortlich,
}: {
  projectId: string;
  intakeFields: IntakeHandoverFields;
  canEdit: boolean;
  isReader: boolean;
  onSaved: () => void;
  embedded?: boolean;
  verantwortlich?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { user } = useSession();

  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const users = usersQuery.data?.items ?? [];

  const [draft, setDraft] = useState<Draft>(() => buildDraft(intakeFields));

  const ui = intakeFields.uebergabe_intake ?? {};
  const dirty =
    (['vertrieb', 'fachl_ap', 'sparte', 'mb_ansprechpartner', 'sonstiges'] as const).some(
      (k) => (draft[k] ?? '') !== (ui[k] ?? ''),
    ) || draft.qm_user_id !== (ui.qm_user_id ?? null);
  const qmBisher = !draft.qm_user_id ? ui.qm?.trim() : undefined;

  // „Zuletzt geändert von X am Y" (Block-Ebene).
  const updatedByName =
    ui.updated_by != null
      ? (users.find((u) => u.user_id === ui.updated_by)?.name ??
        t('v2.intake.unknown_user', 'unbekannt'))
      : null;
  const updatedAtLabel = ui.updated_at
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(ui.updated_at),
      )
    : null;

  const save = useMutation({
    mutationFn: () => {
      // #83: Altschlüssel ms_pl/fbl aus älteren Datensätzen nicht zurückschreiben.
      const {
        ms_pl: _msPl,
        fbl: _fbl,
        // #116: Freitext-QM ist Altbestand und wird nicht zurückgeschrieben.
        qm: _qm,
        ...restIntake
      } = (intakeFields.uebergabe_intake ?? {}) as Record<string, unknown>;
      const merged: IntakeHandoverFields = {
        ...intakeFields,
        uebergabe_intake: {
          ...(restIntake as IntakeHandoverFields['uebergabe_intake']),
          vertrieb: draft.vertrieb.trim(),
          fachl_ap: draft.fachl_ap.trim(),
          sparte: draft.sparte.trim(),
          qm_user_id: draft.qm_user_id,
          mb_ansprechpartner: draft.mb_ansprechpartner.trim(),
          sonstiges: draft.sonstiges.trim(),
          updated_at: new Date().toISOString(),
          updated_by: user?.user_id,
        },
      };
      return api.patchProject(projectId, { intake_handover_fields: merged });
    },
    onSuccess: () => {
      toast.success(t('v2.intake.toast_saved', 'Intake-Notizen gespeichert'));
      onSaved();
    },
    onError: (err) =>
      toast.error(
        t('v2.intake.toast_failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const readerTooltip = isReader ? t('v2.reader.tooltip', 'Sie haben Leserechte') : undefined;

  return (
    <BlockShell embedded={embedded}>
      <div className="flex items-baseline justify-between mb-1 gap-3">
        <h3 className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
          {t('v2.intake.title', 'Intake-Notizen')}
        </h3>
        <span className="text-[11px] shrink-0" style={{ color: 'var(--ink-3)' }}>
          {updatedByName && updatedAtLabel
            ? t('v2.intake.last_changed', 'Zuletzt geändert von {{name}} am {{date}}', {
                name: updatedByName,
                date: updatedAtLabel,
              })
            : t('v2.intake.not_yet', 'Noch nicht erfasst')}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        {/* LINKS: optional der Verantwortlich-Slot, darunter 5 Textinputs in Tab-Reihenfolge —
            DOM-Reihenfolge = TEXT_FIELDS-Reihenfolge, NICHT ändern (Tab-Ordnung), kein order-Utility. */}
        <div className="space-y-3">
          {verantwortlich && (
            // Überschrift steht ÜBER dem Kasten wie die Feld-Labels darunter (#83, Abnahme).
            <div>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-xs" style={{ color: 'var(--ink-2)' }}>
                  {t('v2.intake.verantwortlich_title', 'Verantwortlich')}
                </span>
                <span className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
                  {t('v2.intake.verantwortlich_hint', 'wird sofort gespeichert')}
                </span>
              </div>
              <div
                className="rounded-lg px-3 py-1"
                style={{ border: '1px solid var(--line-soft)', background: 'var(--surface)' }}
              >
                {verantwortlich}
              </div>
            </div>
          )}
          <div
            className={embedded ? 'grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2' : 'space-y-3'}
          >
            {TEXT_FIELDS.map((f) => (
              <Fragment key={f}>
                <div>
                  <label
                    htmlFor={`intake-${f}`}
                    className="block text-xs mb-1"
                    style={{ color: 'var(--ink-2)' }}
                  >
                    {t(`v2.intake.field_${f}`, fieldFallbackLabel(f))}
                  </label>
                  <input
                    id={`intake-${f}`}
                    type="text"
                    value={draft[f]}
                    onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))}
                    disabled={!canEdit}
                    title={readerTooltip}
                    placeholder={
                      f === 'mb_ansprechpartner'
                        ? t('v2.intake.mb_ansprechpartner_ph', 'Wer macht das MB? (interner MA)')
                        : undefined
                    }
                    className="w-full text-sm px-3 py-2 rounded-lg outline-none transition-colors"
                    style={{
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      border: '1px solid var(--line-soft)',
                      opacity: canEdit ? 1 : 0.6,
                      cursor: canEdit ? 'text' : 'not-allowed',
                    }}
                  />
                </div>
                {f === QM_NACH && (
                  <div data-testid="intake-qm">
                    <div
                      className="rounded-lg px-3 py-1"
                      style={{ border: '1px solid var(--line-soft)', background: 'var(--surface)' }}
                    >
                      <AssignableRow
                        label={t('v2.intake.field_qm', 'QM Verantwortlicher')}
                        userId={draft.qm_user_id}
                        users={users}
                        canEdit={canEdit}
                        onAssign={(id) => setDraft((d) => ({ ...d, qm_user_id: id }))}
                      />
                    </div>
                    {qmBisher && (
                      <p className="text-[11px] mt-1" style={{ color: 'var(--ink-3)' }}>
                        {t('v2.intake.qm_bisher', 'bisher: {{text}}', { text: qmBisher })}
                      </p>
                    )}
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </div>

        {/* RECHTS: sonstiges — im DOM nach dem Picker-Slot + den 5 Textinputs, dadurch letzter
            Tab-Stopp (kein tabIndex gesetzt, reine Quellreihenfolge). */}
        <div>
          <label
            htmlFor="intake-sonstiges"
            className="block text-xs mb-1"
            style={{ color: 'var(--ink-2)' }}
          >
            {t('v2.intake.field_sonstiges', 'Sonstiges')}
          </label>
          <textarea
            id="intake-sonstiges"
            value={draft.sonstiges}
            onChange={(e) => setDraft((d) => ({ ...d, sonstiges: e.target.value }))}
            disabled={!canEdit}
            title={readerTooltip}
            placeholder={t('v2.intake.sonstiges_ph', 'Freitext: alles Weitere zur Übergabe …')}
            rows={embedded ? 5 : 12}
            className={`w-full h-[calc(100%-1.5rem)] ${embedded ? 'min-h-[7rem]' : 'min-h-[12rem]'} text-sm px-3 py-2 rounded-lg outline-none transition-colors`}
            style={{
              background: 'var(--surface)',
              color: 'var(--ink)',
              border: '1px solid var(--line-soft)',
              opacity: canEdit ? 1 : 0.6,
              cursor: canEdit ? 'text' : 'not-allowed',
            }}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-3">
        {dirty && (
          <button
            type="button"
            onClick={() => setDraft(buildDraft(intakeFields))}
            className="text-xs px-3 py-1.5 rounded-md"
            style={{
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              border: '1px solid var(--line-soft)',
            }}
          >
            {t('v2.modal.cancel', 'Abbrechen')}
          </button>
        )}
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!canEdit || !dirty || save.isPending}
          title={readerTooltip}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: 'var(--primary)',
            color: 'var(--on-primary)',
            border: 'none',
            opacity: !canEdit || !dirty || save.isPending ? 0.5 : 1,
            cursor: !canEdit ? 'not-allowed' : 'pointer',
          }}
        >
          {save.isPending ? t('v2.intake.saving', 'Speichert…') : t('common.save', 'Speichern')}
        </button>
      </div>
    </BlockShell>
  );
}

function fieldFallbackLabel(key: TextFieldKey): string {
  switch (key) {
    case 'vertrieb':
      return 'Vertrieb';
    case 'fachl_ap':
      return 'Fachlicher Ansprechpartner';
    case 'sparte':
      return 'Dienstleistungsart';
    case 'mb_ansprechpartner':
      return 'Mission Briefing';
  }
}
